import { checkAccountSwitch, login } from '@finn/ua-auth';
import { ModalKey, useCloseModal } from '@finn/ua-modals';
import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { getSession, trackEvent, useCurrentLocale } from '@finn/ui-utils';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import { useLoginStore } from '../store/useLoginStore';

export const useLoginHandler = () => {
  const { isUSA } = useCurrentLocale();
  const track = useTrackingStore((state) => state.track);
  const trackSubmitClick = useCallback(
    (data: object) => {
      track(TrackingEventName.CTA_CLICKED, {
        location: 'login',
        additionalProps: {
          authenticationMethod: 'email',
          ...(data || {}),
        },
      });
    },
    [track]
  );
  const trackFailure = useCallback(
    (error = '', data: object) => {
      track(TrackingEventName.ACCOUNT_LOGIN_FAILED, {
        additionalProps: {
          authenticationMethod: 'email',
          error,
          ...(data || {}),
        },
      });
    },
    [track]
  );
  const form = useFormContext();
  const setServerErrorCode = useLoginStore((state) => state.setServerErrorCode);
  const setFormValues = useLoginStore((state) => state.setFormValues);
  const setSubmittedLogin = useLoginStore((state) => state.setSubmittedLogin);
  const closeModal = useCloseModal();

  return useCallback(async () => {
    setFormValues(form?.getValues() || {});
    const email = form?.getValues('email') || '';
    const password = form?.getValues('password') || '';
    setServerErrorCode(null);
    trackSubmitClick({ source: 'unified-login' });
    setSubmittedLogin(true);
    const result = await login(email, password);
    if (result.ok) {
      const session = await getSession();
      closeModal(ModalKey.LOGIN);
      if (session) {
        trackEvent('Account Logged In', {
          contact_id: session.user.hs_contact_id || '',
          origin: 'checkout',
          loginMethod: 'email',
        });
        await checkAccountSwitch({ session });
      }
    } else {
      trackFailure(result.error ?? 'Login error', { source: 'unified-login' });
      setServerErrorCode(result.error);
    }
    setSubmittedLogin(false);
  }, [
    setFormValues,
    form,
    setServerErrorCode,
    trackSubmitClick,
    setSubmittedLogin,
    isUSA,
    trackFailure,
  ]);
};
